import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiMenu, FiX } from "react-icons/fi";

const navItems = [
  { label: "Home", href: "#preview-hero" },
  { label: "Categories", href: "#preview-services" },
  { label: "Gallery", href: "#preview-gallery" },
  { label: "Contact", href: "#preview-contact" },
];

const banquetMenuItems = [
  { label: "Explore Banquets", href: "/banquets" },
  { label: "Search Your Banquet", href: "/banquets" },
  { label: "Banquet Login", href: "/banquet-owner/login?type=banquet" },
  { label: "Super Admin", href: "/banquet-owner/login?type=admin" },
];

export default function HomePreviewHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [banquetsMenuOpen, setBanquetsMenuOpen] = useState(false);
  const banquetsDropdownRef = useRef(null);
  const navigate = useNavigate();

  const handleClose = () => {
    setMenuOpen(false);
    setBanquetsMenuOpen(false);
  };

  const toggleMenu = () => {
    setMenuOpen((value) => {
      if (value) {
        setBanquetsMenuOpen(false);
      }
      return !value;
    });
  };

  const openBanquetMenuItem = (event, href) => {
    event.preventDefault();
    event.stopPropagation();
    setBanquetsMenuOpen(false);
    setMenuOpen(false);
    navigate(href);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (banquetsDropdownRef.current && !banquetsDropdownRef.current.contains(event.target)) {
        setBanquetsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setBanquetsMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-zoom-gold/15 bg-zoom-warm/92 shadow-hp-xs backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
        <motion.div
          className="min-w-0"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-zoom-gold">Delhi NCR Events</p>
          <p className="font-display text-2xl font-bold tracking-tight text-zoom-emeraldDeep">DecorZoom</p>
          <p className="mt-0.5 hidden text-xs font-medium text-zoom-charcoal/55 sm:block">Banquet, decor and celebration support</p>
        </motion.div>

        <nav className="hidden items-center gap-6 lg:flex">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="text-sm font-medium text-zoom-charcoal/70 transition hover:text-zoom-gold">
              {item.label}
            </a>
          ))}
          <div
            ref={banquetsDropdownRef}
            className="relative"
            onMouseEnter={() => setBanquetsMenuOpen(true)}
          >
            <button
              type="button"
              onClick={() => setBanquetsMenuOpen((value) => !value)}
              className="inline-flex min-h-[44px] items-center rounded-full bg-zoom-emeraldDeep px-4 py-2.5 text-sm font-semibold text-white shadow-hp-emerald transition hover:-translate-y-0.5 hover:bg-zoom-emerald"
              aria-expanded={banquetsMenuOpen}
              aria-haspopup="menu"
            >
              Banquets ▼
            </button>
            {banquetsMenuOpen ? (
              <div className="absolute right-0 top-full z-[80] w-60 pt-2">
                <div className="overflow-hidden rounded-2xl border border-zoom-emerald/15 bg-white py-2 shadow-hp-lg">
                  {banquetMenuItems.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="block min-h-[44px] touch-manipulation px-4 py-3 text-sm font-semibold text-zoom-charcoal/80 hover:bg-zoom-warm hover:text-zoom-emeraldDeep"
                      onClick={(event) => openBanquetMenuItem(event, item.href)}
                      onTouchEnd={(event) => openBanquetMenuItem(event, item.href)}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <Link
            to="/banquets"
            className="inline-flex min-h-[44px] items-center rounded-full border border-zoom-emerald/20 bg-white/80 px-4 py-2.5 text-sm font-semibold text-zoom-emeraldDeep shadow-hp-xs transition hover:-translate-y-0.5 hover:border-zoom-gold hover:bg-white"
          >
            Search Banquets
          </Link>
        </nav>

        <button
          type="button"
          onClick={toggleMenu}
          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full border border-zoom-emerald/15 bg-white/80 text-zoom-emeraldDeep shadow-hp-xs lg:hidden"
          aria-label="Toggle preview menu"
        >
          {menuOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      {menuOpen ? (
        <div className="border-t border-zoom-gold/15 bg-zoom-warm px-4 py-4 shadow-lg lg:hidden">
          <div className="space-y-3">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={handleClose}
                className="block min-h-[44px] rounded-2xl bg-white/75 px-3 py-3 text-sm font-medium text-zoom-emeraldDeep transition hover:bg-white"
              >
                {item.label}
              </a>
            ))}
            <button
              type="button"
              onClick={() => setBanquetsMenuOpen((value) => !value)}
              className="block min-h-[44px] w-full rounded-2xl bg-zoom-emeraldDeep px-3 py-3 text-center text-sm font-semibold text-white"
              aria-expanded={banquetsMenuOpen}
              aria-haspopup="menu"
            >
              Banquets ▼
            </button>
            {banquetsMenuOpen ? (
              <div className="space-y-2 rounded-2xl border border-zoom-emerald/15 bg-white/70 p-2">
                {banquetMenuItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="block min-h-[48px] touch-manipulation rounded-xl bg-white px-4 py-3 text-center text-sm font-semibold text-zoom-emeraldDeep"
                    onClick={(event) => openBanquetMenuItem(event, item.href)}
                    onTouchEnd={(event) => openBanquetMenuItem(event, item.href)}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            ) : null}
            <Link
              to="/banquets"
              onClick={handleClose}
              className="block min-h-[44px] rounded-2xl border border-zoom-emerald/20 bg-white px-3 py-3 text-sm font-semibold text-zoom-emeraldDeep"
            >
              Search Banquets
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
}
